import React from 'react'
import { NavigateFunction } from 'react-router-dom'
import { ActionPlan, ActionPlanStatus, Cost, CostCategory, FinancialYear, UserAccount } from '../api/schemas/schema'
import { CostTypeFilter } from '../components/riskEconomy/CostFilters'
import { IDataContext } from '../interfaces/IDataContext'
import { api } from './apiHelper'
import { isValidEnumValue, translateEnumValue } from './enumHelper'
import { handleMessage } from './stateHelper'
import { IItemState } from '../interfaces/IItemState'
import { t } from '../i18n/i18n'
import { TableColumnDefinition } from '@fluentui/react-components'
import { BadgeCostCategory } from '../components/riskEconomy/BadgeCostCategory'
import { formatToSwedishCrowns } from './stringHelper'
import { BadgeActionPlanStatus } from '../components/demandAnalysis/BadgeActionPlanStatus'
import { ISummaryField } from '../interfaces/ISummaryField'
import { NavigationLink } from '../interfaces/INavigationLink'
import { Text } from '../components/common/text/Text'

export const fetchFinancialYear = async (
   dataContext: IDataContext,
   financialYearId: string,
   navigate: NavigateFunction,
   setFinancialYearState: React.Dispatch<React.SetStateAction<IItemState<FinancialYear>>>
) => {
   try {
      const { organisation } = dataContext.state
      const financialYear = (await api().getFinancialYearById(financialYearId, organisation.id)).data

      setFinancialYearState((prev) => ({
         ...prev,
         item: financialYear,
         loading: false,
      }))
   } catch (error: any) {
      setFinancialYearState((prev) => ({ ...prev, loading: false, error: { message: error.error.message, name: error.status } }))
      navigate(NavigationLink.RiskEconomy)
      handleMessage(dataContext.setRootState, 'error', t('FetchFailed'), t('CouldNotFetchItem', { Item: t('FinancialYear').toLowerCase() }))
   }
}
export const filterCosts = (costs: Cost[], selectedCategory: string, selectedCostType: string): Cost[] => {
   let items = costs
   if (selectedCategory && isValidEnumValue(CostCategory, selectedCategory))
      items = costs.filter((c) => translateEnumValue(CostCategory, c.post.costCategory) === selectedCategory)

   if (selectedCostType && isValidEnumValue(CostTypeFilter, selectedCostType))
      switch (selectedCostType) {
         case translateEnumValue(CostTypeFilter, CostTypeFilter.Budget):
            items = items.filter((i) => i.budget > 0)
            break
         case translateEnumValue(CostTypeFilter, CostTypeFilter.Result):
            items = items.filter((i) => i.result > 0)
            break
      }
   return items
}

export const saveCost = async (
   setFinancialYearState: React.Dispatch<React.SetStateAction<IItemState<FinancialYear>>>,
   dataContext: IDataContext,
   cost: Cost,
   financialYearId: string,
   reset: () => void,
   close: () => void
) => {
   try {
      const { userAccount, organisation } = dataContext.state

      let updatedFinancialYear: FinancialYear
      cost.modifiedById = userAccount.id
      cost.modifiedBy = userAccount

      if (cost.id) {
         const response = (await api().updateCostOnFinancialYear(financialYearId, cost)).data
         updatedFinancialYear = response.updatedFinancialYear
      } else {
         cost.organisationId = organisation.id
         cost.createdById = userAccount.id
         cost.createdBy = userAccount
         updatedFinancialYear = (await api().addCostToFinancialYear(financialYearId, cost)).data
      }

      setFinancialYearState((prev) => ({ ...prev, item: updatedFinancialYear }))
      reset()
      close()
      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyCreated', { Item: t('Cost') }))
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('SomethingWentWrong'), t('CouldNotCreateItem', { Item: t('Cost') }))
   }
}

export const deleteCost = async (
   cost: string,
   dataContext: IDataContext,
   setFinancialYearState: React.Dispatch<React.SetStateAction<IItemState<FinancialYear>>>
) => {
   try {
      await api().deleteCost(cost)

      setFinancialYearState((prev) => ({
         ...prev,
         item: {
            ...prev.item,
            costs: prev.item.costs.filter((c) => c.id !== cost),
         },
      }))
      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyDeleted', { Item: t('Cost') }))
   } catch (error: any) {
      let errorMessage = t('CouldNotDeleteItem', { Item: t('Cost') })
      switch (error.status) {
         case 400:
            errorMessage = t('NoMatchingItemFound', { Item: t('Cost').toLowerCase() })
            break
         case 403:
            errorMessage = t('UserNotAllowedToActionItem', { Action: t('Delete').toLowerCase(), Item: t('TheCost').toLowerCase() })
            break
         case 409:
            errorMessage = t('ItemIsConnectedToItemAndCantBeDeleted', {
               ItemA: t('TheCost'),
               Article: t('A').toLowerCase(),
               ItemB: t('ActionPlan').toLowerCase(),
            })
            break
      }
      handleMessage(dataContext.setRootState, 'error', t('CouldNotDeleteItem', { Item: t('Cost') }), errorMessage)
   }
}

export const fetchPosts = async (dataContext: IDataContext) => {
   const posts = (await api().getAllPosts()).data

   dataContext.setRootState((prev) => ({ ...prev, posts }))
}

export const getCostActionPlanColumns = (actionPlans: ActionPlan[]): TableColumnDefinition<Cost>[] => [
   {
      columnId: 'proposedAction',
      renderHeaderCell: () => t('ActionProposal'),
      renderCell: (item) => {
         return item.proposedAction
      },
      compare: (a, b) => {
         return a.proposedAction.localeCompare(b.proposedAction)
      },
   },
   {
      columnId: 'category',
      renderHeaderCell: () => t('Category'),
      renderCell: (item) => {
         return <BadgeCostCategory category={item.post.costCategory} />
      },
      compare: (a, b) => {
         return translateEnumValue(CostCategory, a.post.costCategory).localeCompare(translateEnumValue(CostCategory, b.post.costCategory))
      },
   },
   {
      columnId: 'estimatedCost',
      renderHeaderCell: () => t('EstimatedCostShort'),
      renderCell: (item) => {
         return item.estimatedCost || item.estimatedCost === 0 ? formatToSwedishCrowns(item.estimatedCost) + ' kr' : <i>{t('NoValueSet')}</i>
      },
      compare: (a, b) => {
         return a.estimatedCost - b.estimatedCost
      },
   },
   {
      columnId: 'budgetedCost',
      renderHeaderCell: () => t('BudgetedCost'),
      renderCell: (item) => {
         const actionPlan = actionPlans.find((a) => a.costId === item.id)

         if (actionPlan.status !== ActionPlanStatus.Decided) {
            return <i>{t('NoValueSet')}</i>
         }

         if (actionPlan.budgetedCost === null) {
            return <i>{t('NoValueSet')}</i>
         }

         return `${formatToSwedishCrowns(actionPlan.budgetedCost)} kr`
      },
      compare: (a, b) => {
         const aActionPlan = actionPlans.find((action) => action.costId === a.id)
         const bActionPlan = actionPlans.find((action) => action.costId === b.id)
         return (aActionPlan?.budgetedCost ?? 0) - (bActionPlan?.budgetedCost ?? 0)
      },
   },
   {
      columnId: 'status',
      renderHeaderCell: () => t('DecisionStatus'),
      renderCell: (item) => {
         const actionPlan = actionPlans.find((a) => a.costId === item.id)
         return <BadgeActionPlanStatus status={actionPlan ? actionPlan.status : ActionPlanStatus.NotDecided} />
      },
      compare: (a, b) => {
         const aStatus: ActionPlanStatus = actionPlans.find((action) => action.costId === a.id).status ?? ActionPlanStatus.NotDecided
         const bStatus: ActionPlanStatus = actionPlans.find((action) => action.costId === b.id).status ?? ActionPlanStatus.NotDecided
         return translateEnumValue(ActionPlanStatus, aStatus).localeCompare(translateEnumValue(ActionPlanStatus, bStatus))
      },
   },
]
export const getFinancialYearActionPlanSummaryFields = (filteredCosts: Cost[], actionPlans: ActionPlan[]): ISummaryField[] => {
   let totEstimatedCost = 0
   let hasEstimated = false
   let totBudgetedCost = 0
   let hasBudgeted = false

   actionPlans.forEach((plan) => {
      const costs = filteredCosts.find((c) => c.id === plan.costId)

      if (costs?.estimatedCost != null) {
         totEstimatedCost += costs.estimatedCost
         hasEstimated = true
      }

      if (plan.status === ActionPlanStatus.Decided && plan.budgetedCost != null) {
         totBudgetedCost += plan.budgetedCost
         hasBudgeted = true
      }
   })

   const totalAnswered = actionPlans.filter((plan) => plan.status !== ActionPlanStatus.NotDecided).length
   const totalItems = actionPlans.length

   return [
      {
         label: t('TotalEstimatedCost'),
         text: hasEstimated ? `${formatToSwedishCrowns(totEstimatedCost)} kr` : <Text italic>{t('NoValueSet')}</Text>,
      },
      {
         label: t('TotalBudgetedCost'),
         text: hasBudgeted ? `${formatToSwedishCrowns(totBudgetedCost)} kr` : <Text italic>{t('NoValueSet')}</Text>,
      },
      { label: t('DecidedMulti'), text: `${totalAnswered} / ${totalItems}` },
   ]
}

export const getFinancialYearActionPlanDefaultValues = (costId: string) => ({
   costId,
   status: ActionPlanStatus.NotDecided,
   actionNeeded: true,
})

export const isFinancialYearOwnerOrCreator = (financialYear: FinancialYear, userAccount: UserAccount) => {
   if (financialYear.createdById === userAccount.id) return true
   if (financialYear.ownerId === userAccount.id) return true
   return false
}
